import React, { useState } from "react";
import { ChevronRight } from "lucide-react";
import {
    formatAuditAction,
    getAuditCategory,
    relativeTime,
} from "../../utils/auditFormat";
import "./AuditEventRow.css";

function summary(metadata) {
    if (!metadata || typeof metadata !== "object") return null;
    const parts = [];
    if (metadata.method) parts.push(`method: ${metadata.method}`);
    if (metadata.reason && typeof metadata.reason === "string") {
        const r = metadata.reason.length > 60
            ? metadata.reason.slice(0, 57) + "…"
            : metadata.reason;
        parts.push(`reason: "${r}"`);
    }
    if (metadata.error_code) parts.push(`error: ${metadata.error_code}`);
    if (metadata.endpoint) parts.push(metadata.endpoint);
    if (metadata.fitted_count != null) parts.push(`${metadata.fitted_count} fitted`);
    if (metadata.duration_ms != null) parts.push(`${metadata.duration_ms}ms`);
    return parts.join(" · ") || null;
}

function AuditEventRow({ event, expandable = true }) {
    const [open, setOpen] = useState(false);
    const meta = formatAuditAction(event.action);
    const category = getAuditCategory(event.action);
    const Icon = meta.icon;
    const meta_summary = summary(event.metadata);

    return (
        <div
            className={`audit-row audit-row--${meta.tone} audit-row--cat-${category}`}
        >
            <button
                type="button"
                className="audit-row__head focus-ring"
                onClick={() => expandable && setOpen((o) => !o)}
                aria-expanded={expandable ? open : undefined}
                disabled={!expandable}
            >
                <span className="audit-row__icon" aria-hidden="true">
                    <Icon size={14} />
                </span>
                <div className="audit-row__main">
                    <div className="audit-row__line">
                        <span className="audit-row__label">{meta.label}</span>
                        {meta_summary && (
                            <span className="audit-row__summary mono">
                                {meta_summary}
                            </span>
                        )}
                    </div>
                    <div className="audit-row__sub">
                        <span className="mono audit-row__action">{event.action}</span>
                        <span className="audit-row__sep">·</span>
                        <span className="mono">{relativeTime(event.created_at)}</span>
                        {event.ip && (
                            <>
                                <span className="audit-row__sep">·</span>
                                <span className="mono">{event.ip}</span>
                            </>
                        )}
                    </div>
                </div>
                {expandable && (
                    <span
                        className={`audit-row__chev ${open ? "is-open" : ""}`}
                        aria-hidden="true"
                    >
                        <ChevronRight size={14} />
                    </span>
                )}
            </button>

            {expandable && open && (
                <div className="audit-row__details">
                    <div className="audit-row__detail">
                        <span className="uppercase-label">Created</span>
                        <span className="mono">
                            {new Date(event.created_at).toLocaleString()}
                        </span>
                    </div>
                    {event.actor_user_id && (
                        <div className="audit-row__detail">
                            <span className="uppercase-label">Actor</span>
                            <span className="mono audit-row__id">
                                {event.actor_user_id}
                            </span>
                        </div>
                    )}
                    {event.target_type && (
                        <div className="audit-row__detail">
                            <span className="uppercase-label">Target</span>
                            <span className="mono">
                                {event.target_type}
                                {event.target_id ? ` · ${event.target_id}` : ""}
                            </span>
                        </div>
                    )}
                    {event.user_agent && (
                        <div className="audit-row__detail">
                            <span className="uppercase-label">User agent</span>
                            <span className="mono audit-row__ua">{event.user_agent}</span>
                        </div>
                    )}
                    {event.request_id && (
                        <div className="audit-row__detail">
                            <span className="uppercase-label">Request id</span>
                            <span className="mono">{event.request_id}</span>
                        </div>
                    )}
                    {event.metadata && Object.keys(event.metadata).length > 0 && (
                        <div className="audit-row__detail audit-row__detail--block">
                            <span className="uppercase-label">Metadata</span>
                            <pre className="audit-row__json">
                                {JSON.stringify(event.metadata, null, 2)}
                            </pre>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default AuditEventRow;
